import * as React from 'react';
import {A11y, Pagination, Scrollbar, Thumbs, Zoom} from 'swiper';
import {faPlayCircle, faSearchPlus} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Swiper, SwiperSlide} from 'swiper/react';
import {useMediaQuery} from 'usehooks-ts';
import {useMemo, useState} from 'react';

import {Alerts} from '../../ui/Alerts/Alerts';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {ImageZoomModal} from '../image-zoom-modal/ImageZoomModal';
import {Item} from '../item.class';

const _xRatio = 4.4;
const GA_TIMEOUT = 2000;

interface PrimaryImageProps {
    item: Item;
}

export const PrimaryImage = ({item}: PrimaryImageProps) => {
    const [imageZoomIndex, setImageZoomIndex] = useState(0);
    const [showImageZoom, setShowImageZoom] = useState(false);
    const [showZoom, setShowZoom] = useState(false);
    const [startDate, setStartDate] = useState<Date>();
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [zoomBackPos, setZoomBackPos] = useState(`0`);
    const [zoomSource, setZoomSource] = useState(item.imagePath);
    const isDisabled = useMediaQuery(`(max-width: 1024px)`);

    // TBD
    const sourceImageRefs: React.RefObject<HTMLImageElement>[] = useMemo(() => {
        const refs = [React.createRef<HTMLImageElement>()];
        item.secondaryImages?.forEach((secondaryImage) => {
            if (secondaryImage) {
                refs.push(React.createRef<HTMLImageElement>());
            }
        });
        return refs;
    }, [item]);

    // TBD
    const lensRefs: React.RefObject<HTMLDivElement>[] = useMemo(() => {
        const refs = [React.createRef<HTMLImageElement>()];
        item.secondaryImages?.forEach((secondaryImage) => {
            if (secondaryImage) {
                refs.push(React.createRef<HTMLImageElement>());
            }
        });
        return refs;
    }, [item]);

    /**
     * Hide the lens and zoom box
     * @param lensRef
     */
    const doHideLens = (lensRef: React.RefObject<HTMLDivElement>) => {
        lensRef.current.style.display = `none`;
        setShowZoom(false);
        if (startDate) {
            const hoverTime = new Date().valueOf() - startDate.valueOf();
            setStartDate(undefined);
            if (hoverTime >= GA_TIMEOUT) {
                gaLegacyCustomEvent({
                    eventAction: `image_magnify`,
                    eventCategory: `IDP`,
                    eventLabel: `${hoverTime / 1000} : ${item.name} : ${item.id}`,
                });
            }
        }
    };

    /**
     * Show the lens and zoom box
     * @param source
     * @param lensRef
     */
    const doShowLens = (source: string, lensRef: React.RefObject<HTMLDivElement>) => {
        if (!source) {
            return;
        }
        if (isDisabled) {
            return;
        }
        setZoomSource(source);
        setShowZoom(true);
        lensRef.current.style.display = `flex`;
        setStartDate(new Date());
    };

    /**
     * Find x,y for cursor inside source image
     * @param mouseEvent
     * @param sourceRef
     */
    const _getCursorPos = (
        mouseEvent: React.MouseEvent<HTMLImageElement | HTMLDivElement>,
        sourceRef: React.RefObject<HTMLImageElement>,
    ) => {
        // Get the x and y positions of the image:
        const sourceRect = sourceRef.current.getBoundingClientRect();
        // Calculate the cursor's x and y coordinates, relative to the image:
        let x = mouseEvent.pageX - sourceRect.left;
        let y = mouseEvent.pageY - sourceRect.top;

        // Consider any page scrolling:
        x = x - window.scrollX;
        y = y - window.scrollY;
        return {x, y};
    };

    /**
     * Get the ratio of the lens
     * @param sourceRef
     */
    const getLensRatio = (sourceRef: React.RefObject<HTMLImageElement>) => {
        if (!sourceRef.current?.width) {
            return 0;
        }
        const maxWidth = 550;
        const imageWidth = sourceRef.current.width;
        const ratio = imageWidth / maxWidth;
        return 125 * ratio;
    };

    /**
     * Calculate the position of the lens
     * @param mouseEvent
     * @param sourceRef
     * @param lensRef
     */
    const doMoveLens = (
        mouseEvent: React.MouseEvent<HTMLImageElement | HTMLDivElement>,
        sourceRef: React.RefObject<HTMLImageElement>,
        lensRef: React.RefObject<HTMLDivElement>,
    ) => {
        if (isDisabled) {
            return;
        }
        // Prevent any other actions that may occur when moving over the image
        mouseEvent.preventDefault();

        const sourceOffset = sourceRef.current.offsetLeft;

        const maxWidth = 550;
        const imageWidth = sourceRef.current.width;
        const sourceImageRatio = imageWidth / maxWidth;

        // Get the cursor's x and y positions:
        const pos = _getCursorPos(mouseEvent, sourceRef);
        const lensWidth = getLensRatio(sourceRef);
        // Calculate the position of the lens:
        let x = pos.x - lensWidth / 2;
        let y = pos.y - lensWidth / 2;

        // Prevent the lens from being positioned outside the image:
        if (x > sourceRef.current.width - lensWidth) {
            x = sourceRef.current.width - lensWidth;
        }
        if (x < 0) {
            x = 0;
        }
        if (y > sourceRef.current.height - lensWidth) {
            y = sourceRef.current.height - lensWidth;
        }
        if (y < 0) {
            y = 0;
        }

        // Set the position of the lens:
        lensRef.current.style.left = `${x + sourceOffset}px`;
        lensRef.current.style.top = `${y}px`;

        // Display what the lens "sees":
        setZoomBackPos(`-${x * (1 / sourceImageRatio) * _xRatio}px -${y * (1 / sourceImageRatio) * _xRatio}px`);
    };

    /**
     * Open the image zoom modal
     * @param index
     */
    const openZoomModal = (index: number) => {
        setShowImageZoom(true);
        setImageZoomIndex(index);
    };

    /**
     * Template
     */
    return (
        <div className="lg:-tw-mt-5 tw-relative">
            <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-text-center tw-hidden lg:tw-block tw-text-gray-400 tw-mb-3">
                Hover over image to magnify
            </div>
            <div
                className="tw-absolute tw-left-[464px] tw-top-[-40px] tw-z-[1019] tw-border tw-bg-white tw-box-border tw-w-[550px] tw-h-[550px] tw-justify-center tw-items-center tw-flex"
                style={{
                    backgroundImage: `url(/ProductImageThumbs1000${zoomSource?.replace(/\s/g, '%20')})`,
                    backgroundPosition: zoomBackPos,
                    backgroundSize: `2420px 2420px`,
                    boxSizing: 'border-box',
                    display: showZoom ? `flex` : `none`,
                }}
            />
            <Swiper
                autoHeight={true}
                className="tw-mb-4 [&_.swiper-slide-next]:!tw-pl-16"
                modules={[Pagination, Scrollbar, A11y, Thumbs, Zoom]}
                pagination={{clickable: true, enabled: true, type: `bullets`, bulletClass: `swiper-pagination-bullet md:!tw-hidden`}}
                scrollbar={false}
                slidesPerView={1}
                thumbs={{swiper: thumbsSwiper}}
                touchReleaseOnEdges
            >
                <SwiperSlide>
                    <div className="tw-flex tw-justify-center lg:tw-justify-start tw-relative tw-box-border tw-cursor-none">
                        <img
                            alt={item.name || `Primary Image`}
                            className="tw-cursor-grab swiper-zoom-target tw-w-full tw-h-auto"
                            data-binding="zoom"
                            height="400"
                            id="primaryImage0"
                            onClick={() => {
                                openZoomModal(0);
                            }}
                            onMouseEnter={() => {
                                doShowLens(item.imagePath, lensRefs[0]);
                            }}
                            onMouseLeave={() => {
                                doHideLens(lensRefs[0]);
                            }}
                            onMouseMove={(event) => {
                                doMoveLens(event, sourceImageRefs[0], lensRefs[0]);
                            }}
                            ref={sourceImageRefs[0]}
                            src={`/ProductImageThumbs400${item.imagePath || '/noimage.png'}`}
                            width="400"
                        />
                        <div
                            className="tw-absolute tw-z-[1021] tw-border tw-box-border tw-w-[125px] tw-h-[125px] tw-bg-[rgba(255,255,255,0.5)] [&_em]:tw-leading-[125px] [&_em]:tw-text-gray-650 tw-items-center tw-justify-center"
                            onClick={() => {
                                openZoomModal(0);
                            }}
                            onMouseEnter={() => {
                                doShowLens(item.imagePath, lensRefs[0]);
                            }}
                            onMouseLeave={() => {
                                doHideLens(lensRefs[0]);
                            }}
                            onMouseMove={(event) => {
                                doMoveLens(event, sourceImageRefs[0], lensRefs[0]);
                            }}
                            style={{
                                display: `none`,
                                width: `${getLensRatio(sourceImageRefs[0])}px`,
                                height: `${getLensRatio(sourceImageRefs[0])}px`,
                            }}
                            ref={lensRefs[0]}
                        >
                            <FontAwesomeIcon
                                className="tw-text-gray-400"
                                icon={faSearchPlus}
                                size="3x"
                            />
                        </div>
                    </div>
                    {item.primaryImageDesc && (
                        <Alerts
                            message={item.primaryImageDesc}
                            variant="light"
                        />
                    )}
                </SwiperSlide>
                {item.secondaryImages?.map((secondaryImage, index) => (
                    <SwiperSlide key={index}>
                        {secondaryImage.referenceType === `Supporting_Image51019` && (
                            <div className="tw-h-auto tw-w-full tw-flex-shrink tw-relative tw-block tw-transform-[translateZ(0)]">
                                <div className="tw-flex tw-justify-center tw-relative tw-box-border tw-cursor-none">
                                    <img
                                        alt={secondaryImage._id}
                                        className="tw-cursor-grab tw-w-full tw-h-auto"
                                        height="400"
                                        id={`primaryImage${index + 1}`}
                                        onClick={() => {
                                            openZoomModal(index + 1);
                                        }}
                                        onMouseEnter={() => {
                                            doShowLens(
                                                secondaryImage.thumb400Location.replace(`ProductImageThumbs400`, ``),
                                                lensRefs[index + 1],
                                            );
                                        }}
                                        onMouseLeave={() => {
                                            doHideLens(lensRefs[index + 1]);
                                        }}
                                        onMouseMove={(event) => {
                                            doMoveLens(event, sourceImageRefs[index + 1], lensRefs[index + 1]);
                                        }}
                                        ref={sourceImageRefs[index + 1]}
                                        src={`/${secondaryImage.thumb400Location}`}
                                        width="400"
                                    />
                                    <div
                                        className="tw-absolute tw-z-[1021] tw-border tw-box-border tw-w-[125px] tw-h-[125px] tw-bg-[rgba(255,255,255,0.5)] tw-items-center tw-justify-center [&_em]:tw-leading-[125px] [&_em]:tw-text-gray-650"
                                        onClick={() => {
                                            openZoomModal(index + 1);
                                        }}
                                        onMouseEnter={() => {
                                            doShowLens(
                                                secondaryImage.thumb400Location.replace(`ProductImageThumbs400`, ``),
                                                lensRefs[index + 1],
                                            );
                                        }}
                                        onMouseLeave={() => {
                                            doHideLens(lensRefs[index + 1]);
                                        }}
                                        onMouseMove={(event) => {
                                            doMoveLens(event, sourceImageRefs[index + 1], lensRefs[index + 1]);
                                        }}
                                        ref={lensRefs[index + 1]}
                                        style={{
                                            display: `none`,
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            className="tw-text-gray-400"
                                            icon={faSearchPlus}
                                            size="3x"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                        {secondaryImage.referenceType === `IMPRL_Video` && (
                            <div className="tw-relative tw-block tw-w-full tw-p-0 tw-overflow-hidden tw-pt-[56.25%] tw-mx-auto">
                                <iframe
                                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    className="tw-absolute tw-top-0 tw-bottom-0 tw-left-0 tw-w-full tw-h-full tw-border-0"
                                    src={`https://www.youtube.com/embed/${secondaryImage._id}`}
                                    suppressHydrationWarning
                                ></iframe>
                            </div>
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className="tw-my-5 tw-text-center tw-px-2 tw-hidden md:tw-block [&_.image-zoom]:tw-h-[83px] [&_.image-zoom]:tw-w-[83px] [&_.swiper-slide]:tw-mb-2 [&._swiper-wrapper]:tw-w-full [&_.swiper-wrapper]:tw-z-[1020] [&_.swiper-wrapper]:tw-relative [&._swiper-slide]:tw-bg-white">
                <Swiper
                    allowTouchMove={false}
                    breakpoints={{
                        375: {
                            slidesPerView: 6,
                            spaceBetween: 8,
                        },
                        640: {
                            slidesPerView: 6,
                            spaceBetween: 8,
                        },
                        768: {
                            slidesPerView: 8,
                            spaceBetween: 8,
                        },
                        1024: {
                            slidesPerView: 10,
                            spaceBetween: 8,
                        },
                    }}
                    className="tw-ml-auto tw-mr-auto tw-relative tw-overflow-hidden tw-list-none tw-p-0 tw-z-[1] tw-block [&_.swiper-slide]:tw-border [&_.swiper-slide]:tw-p-2 [&_.swiper-slide]:!tw-w-[87px] [&_.swiper-slide]:tw-h-fit [&_.swiper-slide-thumb-active]:tw-outline [&_.swiper-slide-thumb-active]:tw-outline-gray-600 [&_.swiper-slide-thumb-active]:tw-outline-offset-[-3px]"
                    freeMode
                    onSwiper={setThumbsSwiper}
                    slidesPerView={4}
                    spaceBetween={8}
                    watchSlidesProgress
                    wrapperClass="tw-flex-wrap"
                >
                    <SwiperSlide>
                        <img
                            alt={item.name || `Primary Image`}
                            className="tw-w-full tw-h-auto"
                            height="67"
                            src={`/ProductImageThumbs67${item.imagePath || '/noimage.png'}`}
                            width="67"
                        />
                    </SwiperSlide>
                    {item.secondaryImages?.map((secondaryImage, index) => (
                        <SwiperSlide key={index}>
                            {secondaryImage.referenceType === `Supporting_Image51019` && (
                                <img
                                    alt={secondaryImage._id}
                                    className="tw-w-full tw-h-auto"
                                    data-secondary-image={secondaryImage._id}
                                    height="67"
                                    src={`/${secondaryImage.thumbLocation}`}
                                    width="67"
                                />
                            )}
                            {secondaryImage.referenceType === `IMPRL_Video` && (
                                <div className="tw-flex tw-justify-center tw-items-center tw-w-full">
                                    <div className="tw-relative tw-top-0 tw-bottom-0 tw-left-0 tw-right-0 tw-h-full tw-w-full tw-opacity-100 tw-inline-flex tw-items-center tw-justify-center">
                                        <img
                                            alt={`${item.h1HTML} Video`}
                                            className="tw-w-full tw-h-auto"
                                            data-secondary-image={secondaryImage._id}
                                            data-secondary-video="true"
                                            height="67"
                                            src={`/${secondaryImage.thumbLocation}`}
                                            width="67"
                                        />
                                        <FontAwesomeIcon
                                            className="tw-absolute tw-z-[200] tw-top-1/2 tw-left-1/2 tw-transform-[translate(-50%,-50%)] tw-text-center tw-text-white tw-shadow-[0_2px_0_rgba(0,0,0,0.5)]"
                                            icon={faPlayCircle}
                                            size="3x"
                                            suppressHydrationWarning
                                        />
                                    </div>
                                </div>
                            )}
                        </SwiperSlide>
                    ))}
                    <SwiperSlide
                        className="tw-w-[83px] tw-h-[83px]"
                        onClick={() => {
                            openZoomModal(0);
                        }}
                    >
                        <div className="tw-h-[67px] tw-flex tw-items-center tw-justify-center">
                            <FontAwesomeIcon
                                className="tw-text-gray-400"
                                icon={faSearchPlus}
                                size="3x"
                            />
                        </div>
                    </SwiperSlide>
                </Swiper>
            </div>
            <ImageZoomModal
                activeIndex={imageZoomIndex}
                item={item}
                onHide={() => {
                    setShowImageZoom(false);
                }}
                show={showImageZoom}
            />
        </div>
    );
};
